import React, { FC, useCallback } from 'react';

import { useIntl } from 'react-intl';
import { ContentRect } from 'react-measure';

import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import Animation from 'components/lottie-animations/favorite-loading/favorite-loading';
import SanityBlockRenderer, {
  IBlockContentOverrides,
  ISerializers,
  PassthroughComponent,
} from 'components/sanity-block-renderer';
import noop from 'utils/noop';
import { getFirstStringInLocaleBlockContent } from 'utils/sanity';

import { IMG_FIT_CONTAIN, INCLUDE_BG_IMG } from './constants';
import {
  BackgroundPicture,
  Description,
  FavoriteButton,
  ImgBgContainer,
  LoadingWrapper,
  MeasuredContainer,
  Name,
  OfferInfoContainer,
  OfferPicture,
  StyledOfferBadges,
} from './details-panel-components';
import { OfferDisclaimer } from './offer-disclaimer';
import { RedemptionPanel } from './redemption-panel';
import { IDetailsPanelViewProps } from './types';

const nameBlockContentOverrides: IBlockContentOverrides = {
  normal: Name,
};

const nameBlockSerializers: ISerializers = {
  marks: {
    em: PassthroughComponent,
  },
  container: PassthroughComponent,
};

const descriptionBlockContentOverrides: IBlockContentOverrides = {
  normal: Description,
};

const descriptionBlockSerializers: ISerializers = {
  container: PassthroughComponent,
};

export const DetailsPanelView: FC<IDetailsPanelViewProps> = ({
  offer,
  onRedeemOffer,
  showRestaurantRedemptionButton,
  disableMobileOfferRedemptions,
  disableInRestaurantOfferRedemptions,
  redemptionErrorMessage,
  enableOfferBadges,
  endingIn,
  platforms,
  updateDetailsPanelContentHeight,
  showSignUpToRedeem,
  showMobileRedemptionButton,
  restaurantRedemptionButtonUiType,
  noPossibleRedemption,
  togglePromoCodeOfferRedeemable,
  offerConfigLoading,
  enableFavoriteOffers = false,
  favorited = false,
  favoriteLoading = false,
  handleToggleFavorite = noop,
}) => {
  const onResize = useCallback(
    (contentRect: ContentRect) => updateDetailsPanelContentHeight(contentRect.entry?.height ?? 0),
    [updateDetailsPanelContentHeight]
  );

  const handleFavoriteClick = useCallback(
    event => {
      event.preventDefault();
      event.stopPropagation();
      handleToggleFavorite(offer._id, getFirstStringInLocaleBlockContent(offer.name));
    },
    [handleToggleFavorite, offer]
  );

  const { formatMessage } = useIntl();

  return (
    <MeasuredContainer onResize={onResize}>
      {enableFavoriteOffers && (
        <FavoriteButton>
          <ButtonForIconFav
            data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
            onClick={handleFavoriteClick}
            aria-label={formatMessage({
              id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
            })}
            isTransparent={false}
          >
            {favoriteLoading ? (
              <LoadingWrapper>
                <Animation />
              </LoadingWrapper>
            ) : (
              <FavoriteIcon isFavorite={favorited} />
            )}
          </ButtonForIconFav>
        </FavoriteButton>
      )}
      <ImgBgContainer>
        {INCLUDE_BG_IMG && offer.backgroundImage && (
          <BackgroundPicture alt="" image={offer.backgroundImage} objectFitContain={false} />
        )}
        {offer?.localizedImage?.locale?.app && (
          <OfferPicture
            image={offer?.localizedImage?.locale?.app!}
            alt={offer?.localizedImage?.locale?.imageDescription ?? ''}
            key={offer._id}
            objectFitContain={IMG_FIT_CONTAIN}
          />
        )}
      </ImgBgContainer>
      <OfferInfoContainer data-testid="offer-info">
        {enableOfferBadges && (
          <StyledOfferBadges
            platforms={platforms}
            displayLoginRequiredBadge={false}
            endingIn={endingIn}
          />
        )}
        <SanityBlockRenderer
          blockContentOverrides={nameBlockContentOverrides}
          serializers={nameBlockSerializers}
          content={offer?.name?.localeRaw}
        />
        <SanityBlockRenderer
          blockContentOverrides={descriptionBlockContentOverrides}
          serializers={descriptionBlockSerializers}
          content={offer?.description?.localeRaw}
        />

        <RedemptionPanel
          togglePromoCodeOfferRedeemable={togglePromoCodeOfferRedeemable}
          offer={offer}
          showMobileRedemptionButton={showMobileRedemptionButton}
          disableInRestaurantOfferRedemptions={disableInRestaurantOfferRedemptions}
          disableMobileOfferRedemptions={disableMobileOfferRedemptions}
          showSignUpToRedeem={showSignUpToRedeem}
          showRestaurantRedemptionButton={showRestaurantRedemptionButton}
          onRedeemOffer={onRedeemOffer}
          redemptionErrorMessage={redemptionErrorMessage}
          restaurantRedemptionButtonUiType={restaurantRedemptionButtonUiType}
          noPossibleRedemption={noPossibleRedemption}
          offerConfigLoading={offerConfigLoading}
        />
        <OfferDisclaimer offer={offer} />
      </OfferInfoContainer>
    </MeasuredContainer>
  );
};
